import { supabase, supabaseUrl, supabaseAnonKey } from './supabaseService';
import { serviceConnectionsService } from './serviceConnectionsService';

export interface ResendConfig {
  apiKey: string;
  isConfigured: boolean;
  fromEmail?: string;
  fromName?: string;
  lastSync?: Date;
}

export interface ResendEmail {
  from: string;
  to: string | string[];
  subject: string;
  html?: string;
  text?: string;
  cc?: string | string[];
  bcc?: string | string[];
  reply_to?: string | string[];
  tags?: Array<{ name: string; value: string }>;
  headers?: Record<string, string>;
}

export interface ResendResponse {
  id: string;
  from: string;
  to: string[];
  created_at: string;
}

class ResendService {
  private config: ResendConfig | null = null;
  private readonly baseUrl = 'https://api.resend.com';
  private readonly retryAttempts = 3;
  private readonly retryDelay = 1000;

  constructor() {
    this.loadConfigFromLocalStorage();
  }

  loadConfig(): ResendConfig | null {
    try {
      console.log('📧 Cargando config Resend desde localStorage...');
      const legacyConfig = localStorage.getItem('crm_configuration');

      if (legacyConfig) {
        const parsed = JSON.parse(legacyConfig);

        if (parsed.resendApiKey) {
          const config = {
            apiKey: parsed.resendApiKey,
            isConfigured: parsed.resendIsConfigured || false,
            fromEmail: parsed.resendFromEmail || '',
            fromName: parsed.resendFromName || 'Cero Ideas CRM',
            lastSync: parsed.resendLastSync ? new Date(parsed.resendLastSync) : undefined
          };

          this.config = config;
          console.log('✅ Resend config cargada desde localStorage');
          return config;
        }
      }
    } catch (error) {
      console.error('❌ Error cargando config Resend:', error);
    }

    console.log('ℹ️ No se encontró config Resend en localStorage');
    return null;
  }

  async loadConfigFromDatabase(): Promise<ResendConfig | null> {
    try {
      console.log('📧 Cargando config Resend desde BD...');

      const connection = await serviceConnectionsService.getServiceConnection('resend');

      if (!connection) {
        console.log('ℹ️ No existe config Resend en BD');
        return null;
      }

      const config: ResendConfig = {
        apiKey: connection.apiKey || '',
        isConfigured: connection.isActive,
        fromEmail: connection.configData.fromEmail || '',
        fromName: connection.configData.fromName || 'Cero Ideas CRM',
        lastSync: connection.lastConnectedAt
      };

      this.config = config;
      console.log('✅ Resend config cargada desde BD');
      return config;
    } catch (error) {
      console.error('❌ Error cargando config Resend desde BD:', error);
      return null;
    }
  }

  private loadConfigFromLocalStorage(): ResendConfig | null {
    return this.loadConfig();
  }

  saveConfig(updates: Partial<ResendConfig>): void {
    try {
      const legacyConfig = localStorage.getItem('crm_configuration');
      const currentLegacyConfig = legacyConfig ? JSON.parse(legacyConfig) : {};

      if (updates.apiKey !== undefined) currentLegacyConfig.resendApiKey = updates.apiKey;
      if (updates.isConfigured !== undefined) currentLegacyConfig.resendIsConfigured = updates.isConfigured;
      if (updates.fromEmail !== undefined) currentLegacyConfig.resendFromEmail = updates.fromEmail;
      if (updates.fromName !== undefined) currentLegacyConfig.resendFromName = updates.fromName;
      if (updates.lastSync !== undefined) currentLegacyConfig.resendLastSync = updates.lastSync?.toISOString();

      localStorage.setItem('crm_configuration', JSON.stringify(currentLegacyConfig));

      const currentConfig = this.loadConfig() || {
        apiKey: '',
        isConfigured: false
      };

      const newConfig = { ...currentConfig, ...updates };
      this.config = newConfig;

      console.log('✅ Resend config guardada en localStorage');

      this.saveConfigAsync(updates).catch(error => {
        console.error('❌ Error guardando Resend en BD:', error);
      });
    } catch (error) {
      console.error('❌ Error guardando config Resend:', error);
      throw error;
    }
  }

  async saveConfigAsync(updates: Partial<ResendConfig>): Promise<void> {
    try {
      const currentConfig = this.config || this.loadConfig() || {
        apiKey: '',
        isConfigured: false
      };

      const newConfig = { ...currentConfig, ...updates };

      await serviceConnectionsService.saveServiceConnection(
        'resend',
        newConfig.apiKey,
        {
          fromEmail: newConfig.fromEmail,
          fromName: newConfig.fromName,
          isConfigured: newConfig.isConfigured
        }
      );

      console.log('✅ Resend config guardada en BD');
    } catch (error) {
      console.error('❌ Error en saveConfigAsync Resend:', error);
      throw error;
    }
  }

  async ensureConfigLoaded(): Promise<void> {
    try {
      if (!this.config) {
        this.loadConfigFromLocalStorage();
      }
    } catch (error) {
      console.error('Error ensuring config loaded:', error);
      throw error;
    }
  }

  isConfigured(): boolean {
    const config = this.loadConfigFromLocalStorage();
    const hasValidKey = !!(config?.apiKey && config.apiKey.trim().startsWith('re_') && config.apiKey.length > 20);

    console.log('🔍 Resend isConfigured check:', {
      hasConfig: !!config,
      hasApiKey: !!config?.apiKey,
      startsWithRe: config?.apiKey?.startsWith('re_'),
      keyLength: config?.apiKey?.length,
      isValid: hasValidKey && (config?.isConfigured || false)
    });

    return hasValidKey && (config?.isConfigured || false);
  }

  private async makeRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    data?: any,
    retryCount: number = 0
  ): Promise<any> {
    const config = this.loadConfigFromLocalStorage();
    if (!config?.apiKey) {
      throw new Error('Resend no está configurado. Configura tu API Key en Configuración.');
    }

    if (!config.apiKey.startsWith('re_')) {
      throw new Error('API Key de Resend inválida. Debe empezar con "re_"');
    }

    const url = `${this.baseUrl}${endpoint}`;
    const startTime = Date.now();

    try {
      console.log(`📧 Resend ${method} ${endpoint}${retryCount > 0 ? ` (retry ${retryCount})` : ''}`);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${config.apiKey.trim()}`,
          'Content-Type': 'application/json'
        },
        body: data ? JSON.stringify(data) : undefined
      });

      const duration = Date.now() - startTime;
      let responseData;

      try {
        responseData = await response.json();
      } catch {
        const textResponse = await response.text();
        console.warn('⚠️ Response is not JSON:', textResponse.substring(0, 100));
        responseData = { error: 'Invalid response format', response: textResponse };
      }

      await this.logRequest(endpoint, method, data, responseData, response.status, response.ok, duration);

      if (!response.ok) {
        const errorMessage = typeof responseData === 'object'
          ? responseData.message || responseData.error || `HTTP ${response.status}`
          : responseData;

        console.error(`❌ Resend API Error ${response.status}:`, errorMessage);

        if ((response.status >= 500 || response.status === 429) && retryCount < this.retryAttempts) {
          console.warn(`⚠️ Resend ${method} ${endpoint} failed (${response.status}), retrying in ${this.retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, this.retryDelay * (retryCount + 1)));
          return this.makeRequest(endpoint, method, data, retryCount + 1);
        }

        let specificError = errorMessage;
        if (response.status === 401) {
          specificError = 'API Key inválida o expirada. Verifica tu configuración de Resend.';
        } else if (response.status === 403) {
          specificError = 'API Key sin permisos suficientes.';
        } else if (response.status === 404) {
          specificError = `Recurso no encontrado: ${endpoint}`;
        } else if (response.status === 422) {
          specificError = `Error de validación: ${errorMessage}`;
        }

        throw new Error(`Resend API Error: ${specificError}`);
      }

      console.log(`✅ Resend ${method} ${endpoint} - ${response.status} (${duration}ms)`);
      return responseData;

    } catch (error) {
      const duration = Date.now() - startTime;

      if (retryCount === 0) {
        await this.logRequest(endpoint, method, data, null, 0, false, duration, (error as Error).message);
      }

      if (retryCount < this.retryAttempts && (error instanceof TypeError || (error as Error).message.includes('fetch'))) {
        console.warn(`⚠️ Network error, retrying in ${this.retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * (retryCount + 1)));
        return this.makeRequest(endpoint, method, data, retryCount + 1);
      }

      console.error(`❌ Resend ${method} ${endpoint} failed:`, error);
      throw error;
    }
  }

  private async logRequest(
    endpoint: string,
    method: string,
    requestData: any,
    responseData: any,
    statusCode: number,
    success: boolean,
    duration: number,
    errorMessage?: string
  ): Promise<void> {
    try {
      await supabase
        .from('resend_logs')
        .insert({
          endpoint,
          method,
          request_data: requestData,
          response_data: responseData,
          status_code: statusCode,
          success,
          error_message: errorMessage,
          duration
        });
    } catch (error) {
      console.error('Error logging Resend request:', error);
    }
  }

  async testConnection(): Promise<boolean> {
    try {
      console.log('🧪 Probando conexión con Resend...');

      const config = this.loadConfigFromLocalStorage();
      if (!config?.apiKey) {
        throw new Error('Resend no está configurado. Configura tu API Key en Configuración.');
      }

      if (!config.apiKey.startsWith('re_')) {
        throw new Error('API Key de Resend inválida. Debe empezar con "re_"');
      }

      // Use Edge Function to test connection (avoid CORS issues)
      if (!supabaseUrl) {
        throw new Error('Supabase URL no está configurada');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/resend-test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          apiKey: config.apiKey,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Test de conexión falló');
      }

      console.log('✅ Resend conectado correctamente');
      this.saveConfig({
        isConfigured: true,
        lastSync: new Date()
      });
      return true;
    } catch (error) {
      console.error('❌ Error probando Resend:', (error as Error).message);

      if ((error as Error).message.includes('Invalid API Key')) {
        throw new Error('API Key inválida. Verifica que sea una API Key válida de Resend.');
      } else if ((error as Error).message.includes('Unauthorized') || (error as Error).message.includes('401')) {
        throw new Error('API Key sin permisos suficientes.');
      } else if ((error as Error).message.includes('Forbidden') || (error as Error).message.includes('403')) {
        throw new Error('Permisos insuficientes. Tu API Key no tiene acceso.');
      }

      this.saveConfig({ isConfigured: false });
      throw error;
    }
  }

  async sendEmail(emailData: ResendEmail, options: { recordFailureJob?: boolean } = {}): Promise<ResendResponse> {
    try {
      const config = this.loadConfigFromLocalStorage();
      if (!config) {
        throw new Error('Resend no está configurado');
      }

      const from = emailData.from || `${config.fromName} <${config.fromEmail}>`;

      console.log('📧 Enviando email via Resend:', Array.isArray(emailData.to) ? emailData.to[0] : emailData.to);

      if (!supabaseUrl) {
        throw new Error('Supabase URL no está configurada');
      }

      const payload = {
        from,
        to: Array.isArray(emailData.to) ? emailData.to : [emailData.to],
        subject: emailData.subject,
        html: emailData.html,
        text: emailData.text,
        cc: emailData.cc ? (Array.isArray(emailData.cc) ? emailData.cc : [emailData.cc]) : undefined,
        bcc: emailData.bcc ? (Array.isArray(emailData.bcc) ? emailData.bcc : [emailData.bcc]) : undefined,
        reply_to: emailData.reply_to ? (Array.isArray(emailData.reply_to) ? emailData.reply_to : [emailData.reply_to]) : undefined,
        tags: emailData.tags,
        headers: emailData.headers
      };

      const response = await fetch(`${supabaseUrl}/functions/v1/resend-send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Falló el envío del email');
      }

      console.log('✅ Email enviado via Resend:', result.data.id);
      return result.data;
    } catch (error) {
      console.error('❌ Error enviando email via Resend:', error);
      try {
        const { systemHealthService } = await import('./systemHealthService');
        await systemHealthService.recordSystemEvent(
          'email_send_failed',
          'notifications',
          'error',
          error instanceof Error ? error.message : String(error)
        );
        if (options.recordFailureJob !== false) await systemHealthService.recordJob(
          'send_email',
          {
            to: emailData.to,
            subject: emailData.subject,
            html: emailData.html,
            text: emailData.text,
          },
          error instanceof Error ? error.message : String(error)
        );
      } catch {
        // silent — best effort
      }
      throw error;
    }
  }

  async sendTransactionalEmail(
    to: string | string[],
    subject: string,
    html: string,
    text?: string,
    options?: {
      cc?: string | string[];
      bcc?: string | string[];
      reply_to?: string | string[];
      tags?: Array<{ name: string; value: string }>;
    }
  ): Promise<ResendResponse> {
    const config = this.loadConfigFromLocalStorage();
    if (!config) {
      throw new Error('Resend no está configurado');
    }

    return this.sendEmail({
      from: `${config.fromName} <${config.fromEmail}>`,
      to,
      subject,
      html,
      text,
      ...options
    });
  }

  async sendWelcomeEmail(
    contactEmail: string,
    contactName: string
  ): Promise<ResendResponse> {
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333;">¡Bienvenido, ${contactName}!</h1>
        <p>Gracias por unirte a Cero Ideas CRM.</p>
        <p>Estamos emocionados de tenerte con nosotros.</p>
        <p>Si tienes alguna pregunta, no dudes en contactarnos en contacto@ceroideas.es</p>
        <p>Saludos,<br>El equipo de Cero Ideas</p>
      </div>
    `;

    const text = `¡Bienvenido, ${contactName}!\n\nGracias por unirte a Cero Ideas CRM.\nEstamos emocionados de tenerte con nosotros.\n\nSi tienes alguna pregunta, no dudes en contactarnos en contacto@ceroideas.es\n\nSaludos,\nEl equipo de Cero Ideas`;

    return this.sendTransactionalEmail(
      contactEmail,
      '¡Bienvenido a Cero Ideas CRM!',
      html,
      text,
      {
        tags: [{ name: 'type', value: 'welcome' }]
      }
    );
  }

  async sendMeetingReminderEmail(
    contactEmail: string,
    contactName: string,
    meetingTitle: string,
    meetingDate: Date,
    meetingLink?: string
  ): Promise<ResendResponse> {
    const formattedDate = meetingDate.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const formattedTime = meetingDate.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });

    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333;">Recordatorio de Reunión</h1>
        <p>Hola ${contactName},</p>
        <p>Te recordamos que tienes una reunión programada:</p>
        <div style="background-color: #f5f5f5; padding: 20px; border-radius: 5px; margin: 20px 0;">
          <h2 style="margin-top: 0; color: #333;">${meetingTitle}</h2>
          <p><strong>Fecha:</strong> ${formattedDate}</p>
          <p><strong>Hora:</strong> ${formattedTime}</p>
          ${meetingLink ? `<p><a href="${meetingLink}" style="color: #0066cc;">Unirse a la reunión</a></p>` : ''}
        </div>
        <p>¡Nos vemos pronto!</p>
        <p>Saludos,<br>El equipo de Cero Ideas</p>
      </div>
    `;

    const text = `Recordatorio de Reunión\n\nHola ${contactName},\n\nTe recordamos que tienes una reunión programada:\n\n${meetingTitle}\nFecha: ${formattedDate}\nHora: ${formattedTime}\n${meetingLink ? `\nEnlace: ${meetingLink}` : ''}\n\n¡Nos vemos pronto!\n\nSaludos,\nEl equipo de Cero Ideas`;

    return this.sendTransactionalEmail(
      contactEmail,
      `Recordatorio: ${meetingTitle}`,
      html,
      text,
      {
        tags: [{ name: 'type', value: 'meeting-reminder' }]
      }
    );
  }

  async sendPasswordChangedEmail(
    userEmail: string,
    userName: string
  ): Promise<ResendResponse> {
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333;">Contraseña Cambiada</h1>
        <p>Hola ${userName},</p>
        <p>Tu contraseña ha sido cambiada exitosamente.</p>
        <p>Si no fuiste tú quien realizó este cambio, por favor contacta inmediatamente a nuestro equipo de soporte.</p>
        <p>Saludos,<br>El equipo de Cero Ideas</p>
      </div>
    `;

    const text = `Contraseña Cambiada\n\nHola ${userName},\n\nTu contraseña ha sido cambiada exitosamente.\n\nSi no fuiste tú quien realizó este cambio, por favor contacta inmediatamente a nuestro equipo de soporte.\n\nSaludos,\nEl equipo de Cero Ideas`;

    return this.sendTransactionalEmail(
      userEmail,
      'Contraseña Cambiada - Cero Ideas CRM',
      html,
      text,
      {
        tags: [{ name: 'type', value: 'password-changed' }]
      }
    );
  }

  getLogs(limit: number = 100): any[] {
    try {
      const logs = JSON.parse(localStorage.getItem('resend_logs') || '[]');
      return logs.slice(0, limit).map((log: any) => ({
        ...log,
        createdAt: new Date(log.createdAt)
      }));
    } catch (error) {
      console.error('Error getting logs:', error);
      return [];
    }
  }

  getSyncStats(): any {
    try {
      const logs = this.getLogs();

      const last24h = logs.filter(log =>
        new Date(log.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)
      );

      return {
        totalRequests: logs.length,
        last24h: last24h.length,
        successRate: logs.length > 0 ? (logs.filter(l => l.success).length / logs.length) * 100 : 0,
        averageResponseTime: logs.length > 0 ? logs.reduce((sum, l) => sum + l.duration, 0) / logs.length : 0,
        lastSync: this.config?.lastSync || null
      };
    } catch (error) {
      console.error('Error getting sync stats:', error);
      return {
        totalRequests: 0,
        last24h: 0,
        successRate: 0,
        averageResponseTime: 0,
        lastSync: null
      };
    }
  }
}

export const resendService = new ResendService();
